import styled, { css } from 'styled-components';
import NextLink from 'next/link';

import Link from 'Elements/Link';
import WeekCalendar from 'Elements/WeekCalendar';

export const Container = styled.article`
    ${({ theme }) => css`
        display: flex;
        flex-direction: row;
        position: relative;
        width: 100%;

        @media ${theme.breakpoints.medium.media} {
            display: block;
        }
    `}
`;

export const HiddenLink = styled(NextLink)`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
`;

export const ImageWrapper = styled.div<{ $weekStatus?: string; $isHovering: boolean }>`
    ${({ theme, $weekStatus, $isHovering }) => css`
        position: relative;
        height: 5.25rem;
        min-width: 9.25rem;
        width: auto;
        border-radius: 0.5rem;
        align-self: center;

        ${$weekStatus === 'Current week' &&
        css`
            border: 0.125rem solid ${theme.colors.primary.hex};
        `};

        @media ${theme.breakpoints.medium.media} {
            height: 7.75rem;
        }

        @media ${theme.breakpoints.large.media} {
            height: 11.625rem;
            opacity: ${$isHovering ? 0 : 1};
            transition: opacity 0.4s ease-in-out;
        }
    `}
`;

export const SmallWeekCalendar = styled(WeekCalendar)`
    ${({ theme }) => css`
        width: 2.5rem;
        height: 2.5rem;
        position: absolute;
        z-index: 2;
        left: 0;
        top: 0;
        margin: 0.25rem;
        z-index: 1;

        svg {
            width: 2.5rem;
            height: 2.5rem;
        }

        @media ${theme.breakpoints.large.media} {
            display: none;
        }
    `}
`;

export const StyledWeekCalendar = styled(WeekCalendar)`
    ${({ theme }) => css`
        display: none;

        @media ${theme.breakpoints.large.media} {
            display: inline-block;

            svg {
                width: 3.5rem;
                height: 3.5rem;
            }
        }
    `}
`;

export const WeekStatus = styled.p<{ $weekStatusColour: string }>`
    ${({ theme, $weekStatusColour }) => css`
        display: none;
        position: absolute;
        z-index: 2;
        left: 0;
        bottom: 0;
        margin: 0.25rem;
        color: ${$weekStatusColour};
        background-color: ${$weekStatusColour === '#197A6E'
            ? '#E9F5F3'
            : $weekStatusColour === '#B2000E'
            ? '#FAE8E9'
            : $weekStatusColour === '#7536C3'
            ? '#F4ECFE'
            : theme.colors.secondary.hex};
        padding: 0 0.5rem;
        border-radius: 1.125rem;
        font-size: 0.875rem;

        @media ${theme.breakpoints.medium.media} {
            display: flex;
        }

        @media ${theme.breakpoints.large.media} {
            top: 0;
            bottom: auto;
            font-size: 1rem;
        }
    `}
`;

export const PhoneWeekStatus = styled(WeekStatus)<{ $weekStatusColour: string }>`
    ${({ theme }) => css`
        display: flex;
        position: static;
        z-index: 1;
        left: auto;
        bottom: auto;
        gap: 0.5rem;
        margin: 0;

        @media ${theme.breakpoints.medium.media} {
            display: none;
        }
    `}
`;

export const ContentWrapper = styled.div<{ $isHovering: boolean }>`
    ${({ theme, $isHovering }) => css`
        display: flex;
        flex-direction: column;
        height: ${$isHovering ? '100%' : 'auto'};

        @media ${theme.breakpoints.large.media} {
            position: relative;
            top: ${$isHovering ? '-11.625rem' : '0'};
            transition: top 0.4s ease-in-out;
            pointer-events: none;
        }
    `}
`;

export const HeadingWrapper = styled.div<{ $isHovering: boolean }>`
    ${({ theme, $isHovering }) => css`
        display: flex;
        flex-direction: column;
        align-items: start;
        gap: 0;
        margin: 0 0 0 0.5rem;

        @media ${theme.breakpoints.medium.media} {
            margin: 0.5rem 0 0 0;
            flex-direction: row;
            gap: 0.5rem;
        }

        @media ${theme.breakpoints.large.media} {
            margin: ${$isHovering ? '0 0 0.5rem 0' : '0.5rem 0 0 0'};
        }
    `}
`;

export const Heading = styled.h3`
    ${({ theme }) => css`
        font-size: 1rem;
        color: ${theme.colors.text.hex};
        margin: 0.25rem 0 0 0;
        text-decoration: underline;
        line-height: 1.35rem;

        @media ${theme.breakpoints.medium.media} {
            font-size: 1.25rem;
            margin: 0;
            line-height: 1.75rem;
        }

        @media ${theme.breakpoints.large.media} {
            font-size: 1.5rem;
            text-decoration: none;
            line-height: 1.85rem;
        }
    `}
`;

export const CardLink = styled(Link)`
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    color: inherit;
    text-decoration: none;

    &:before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 2;
    }
`;

export const Description = styled.p<{ $isHovering: boolean }>`
    ${({ theme, $isHovering }) => css`
        display: none;

        @media ${theme.breakpoints.large.media} {
            display: block;
            color: ${theme.colors.text.hex};
            margin: 0;
            height: 0;
            cursor: default;
            opacity: 0;

            ${$isHovering &&
            css`
                opacity: 1;
                transition: opacity 0.6s ease-in-out;
            `}
        }
    `}
`;
